import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Button, TextField, Grid, IconButton, Tooltip,
  Tabs, Tab, Chip, Checkbox, Select, MenuItem, FormControl, InputLabel,
  Dialog, DialogTitle, DialogContent, DialogActions
} from "@mui/material";
import {
  Add as AddIcon, Delete as DeleteIcon, FilterList as FilterIcon,
  UploadFile as UploadIcon, PictureAsPdf as PdfIcon
} from "@mui/icons-material";
import { fetchData, deleteData, postData } from "../../BaseUrl";
import Dynamictablepagination from "../../DynamicComponent/Dynamictablepagination";
import Toast from "../../DynamicComponent/Toast";
import ExportMenu from "../../DynamicComponent/ExportMenu";

const FALLBACK_TEACHERS = [
  { id: "T1", name: "Nalini S" },
  { id: "T2", name: "Kavitha R" },
  { id: "T3", name: "Meena P" },
  { id: "T4", name: "Priya T" },
];

const REPO_TABS = [
  { label: "Question Papers", type: "question_papers" },
  { label: "Handbooks", type: "handbooks" },
  { label: "Modal Questions Papers", type: "modal_question_papers" },
  { label: "Progress Report", type: "progress_report" },
  { label: "Assignments", type: "assignments" }
];

const AdminRepository = () => {
  const [tabValue, setTabValue] = useState(0);
  const [reports, setReports] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, totalPages: 1 });
  const [filters, setFilters] = useState({ search: "" });
  const [selectedItems, setSelectedItems] = useState([]);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadGrade, setUploadGrade] = useState("");
  const [uploadAssignTo, setUploadAssignTo] = useState("");
  const [teachers, setTeachers] = useState(FALLBACK_TEACHERS);

  // Load teacher list for assign-to dropdown
  useEffect(() => {
    fetchData("admin/master/teachers?limit=100")
      .then(res => {
        const list = res?.data?.records || res?.data || [];
        if (Array.isArray(list) && list.length) setTeachers(list.map(t => ({ id: t.id || t._id, name: t.fullName || t.name || t.firstName })));
      })
      .catch(() => {}); // Silently use fallback
  }, []);

  const loadReports = useCallback(async (page = 1) => {
    try {
      const params = new URLSearchParams({ page, limit: 10, type: REPO_TABS[tabValue].type });
      if (filters.search) params.append("search", filters.search);

      const result = await fetchData(`admin/repository?${params.toString()}`);
      if (result?.data) {
        setReports(result.data.records || result.data.reports || result.data || []);
        setPagination(result.data.pagination || { total: 0, page: 1, totalPages: 1 });
      }
    } catch (err) {
      // Use empty state on fetch failure — API may not exist yet
      setReports([]);
    }
  }, [filters, tabValue]);

  useEffect(() => { loadReports(); }, [loadReports]);

  // Reset selection when tab changes
  useEffect(() => { setSelectedItems([]); }, [tabValue]);


  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) return;
    try {
      for (const id of selectedItems) {
        await deleteData(`admin/repository/${id}`);
      }
      setToast({ open: true, severity: "success", message: `${selectedItems.length} item(s) deleted` });
      setSelectedItems([]);
      loadReports();
    } catch (err) {
      setToast({ open: true, severity: "error", message: "Delete failed" });
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadTitle) {
      setToast({ open: true, severity: "warning", message: "Please provide a title and file" });
      return;
    }
    try {
      const form = new FormData();
      form.append("file", uploadFile);
      form.append("title", uploadTitle);
      form.append("type", REPO_TABS[tabValue].type);
      form.append("grade", uploadGrade);
      if (uploadAssignTo) form.append("assignTo", uploadAssignTo);
      await postData("admin/repository/", form);

      // Send notification to assigned teacher — §25
      if (uploadAssignTo) {
        const teacher = teachers.find(t => t.id === uploadAssignTo);
        try {
          await postData("admin/notification/send", {
            recipientId: uploadAssignTo,
            recipientName: teacher?.name || uploadAssignTo,
            message: `A new repository item "${uploadTitle}" (${REPO_TABS[tabValue].label}) has been assigned to you.`,
            type: "repository",
            meta: { type: REPO_TABS[tabValue].type, title: uploadTitle },
          });
        } catch (_) { /* Non-critical — don't fail upload if notification fails */ }
      }

      setToast({ open: true, severity: "success", message: "File uploaded successfully" + (uploadAssignTo ? " and teacher notified" : "") });
      setUploadOpen(false);
      setUploadFile(null);
      setUploadTitle("");
      setUploadGrade("");
      setUploadAssignTo("");
      loadReports();
    } catch {
      setToast({ open: true, severity: "error", message: "Upload failed" });
    }
  };

  const handleFileUpload = (rowId) => {
    setToast({ open: true, severity: "info", message: `Upload triggered for item ${rowId}` });
  };

  const toggleRowSelect = (id) => {
    setSelectedItems(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const columns = [
    { label: "", field: "checkboxCol" },
    { label: "Subject", field: "subject" },
    { label: "Grade", field: "grade" },
    { label: "Term", field: "term" },
    { label: "Question Paper Type", field: "questionPaperType" },
    { label: "Date", field: "date" },
    { label: "Assign To", field: "assignTo" },
    { label: "Upload File", field: "uploadFileCell" },
    { label: "Status", field: "statusChip" }
  ];

  const rows = reports.map((r) => ({
    ...r,
    checkboxCol: (
      <Checkbox
        size="small"
        checked={selectedItems.includes(r.id)}
        onChange={() => toggleRowSelect(r.id)}
      />
    ),
    subject: r.subject || r.reportTitle || r.title || "-",
    grade: r.grade || "-",
    term: r.term || r.academicYear || "-",
    questionPaperType: r.questionPaperType || r.type || "-",
    date: r.date || r.createdAt?.slice(0, 10) || "-",
    assignTo: r.assignTo || r.uploadedBy || r.createdBy || "-",
    uploadFileCell: r.fileName || r.file ? (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Chip
          icon={<PdfIcon sx={{ fontSize: 14 }} />}
          label={r.fileName || r.file || "document.pdf"}
          size="small"
          clickable
          sx={{ backgroundColor: "#fce4ec", color: "#c62828", fontWeight: 500 }}
        />
        <Tooltip title="Re-upload">
          <IconButton size="small" component="label" onClick={() => handleFileUpload(r.id)}>
            <UploadIcon fontSize="small" sx={{ color: "#244C77" }} />
            <input type="file" hidden />
          </IconButton>
        </Tooltip>
      </Box>
    ) : (
      <Tooltip title="Upload File">
        <IconButton size="small" component="label">
          <UploadIcon fontSize="small" sx={{ color: "#244C77" }} />
          <input type="file" hidden onChange={() => handleFileUpload(r.id)} />
        </IconButton>
      </Tooltip>
    ),
    statusChip: (() => {
      const status = r.status || "upcoming";
      const isFinished = status.toLowerCase() === "finished";
      return (
        <Chip
          label={isFinished ? "Finished" : "Upcoming"}
          size="small"
          sx={{
            backgroundColor: isFinished ? "#e8f5e9" : "#fce4ec",
            color: isFinished ? "#4caf50" : "#f44336",
            fontWeight: 600
          }}
        />
      );
    })()
  }));

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="body2" color="text.secondary" mb={1}>Pages / Repository Management</Typography>
      <Typography variant="h5" fontWeight="bold" mb={3}>Repository Management</Typography>

      {/* Top Actions */}
      <Grid container spacing={2} mb={2} alignItems="center">
        <Grid item xs={12} sm={4}>
          <TextField size="small" fullWidth placeholder="Search..."
            value={filters.search} onChange={(e) => setFilters(p => ({ ...p, search: e.target.value }))}
            onKeyDown={(e) => e.key === "Enter" && loadReports()} />
        </Grid>
        <Grid item xs={6} sm={2}>
          <Button variant="outlined" startIcon={<FilterIcon />} onClick={() => loadReports()}
            sx={{ height: 40 }}>Apply</Button>
        </Grid>
        <Grid item xs={12} sm={6} sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          {selectedItems.length > 0 && (
            <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={handleBulkDelete}
              sx={{ textTransform: "none" }}>
              Delete ({selectedItems.length})
            </Button>
          )}
          <ExportMenu
            rows={reports}
            columns={[
              { label: "Subject",    field: "subject" },
              { label: "Grade",      field: "grade" },
              { label: "Term",       field: "term" },
              { label: "Type",       field: "questionPaperType" },
              { label: "Date",       field: "date" },
              { label: "Assign To",  field: "assignTo" },
            ]}
            filename={`repository_${REPO_TABS[tabValue].type}`}
          />
          <Button variant="contained" startIcon={<UploadIcon />}
            onClick={() => setUploadOpen(true)}
            sx={{ background: "linear-gradient(135deg,#4A7BA8,#244C77)", color: "#fff", textTransform: "none" }}>
            Upload
          </Button>
          <Button variant="contained" startIcon={<AddIcon />}
            onClick={() => setToast({ open: true, severity: "info", message: "Add repository item" })}
            sx={{ background: "linear-gradient(90deg, #4A7BA8, #244C77)", textTransform: "none" }}>
            Add
          </Button>
        </Grid>
      </Grid>

      {/* Filter Tabs */}
      <Tabs
        value={tabValue}
        onChange={(_, v) => setTabValue(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          mb: 3,
          borderBottom: "2px solid #e0e0e0",
          "& .MuiTab-root": {
            textTransform: "none",
            fontWeight: 500,
            color: "#757575",
            minWidth: 140
          },
          "& .Mui-selected": {
            color: "#fff !important",
            backgroundColor: "#1a1a2e",
            borderRadius: "6px 6px 0 0",
            fontWeight: 700
          },
          "& .MuiTabs-indicator": {
            backgroundColor: "#244C77",
            height: 3
          }
        }}
      >
        {REPO_TABS.map((tab) => (
          <Tab key={tab.type} label={tab.label} />
        ))}
      </Tabs>

      <Dynamictablepagination columns={columns} rows={rows} page={pagination?.page || 1} totalPages={pagination?.totalPages || 1} handleChangePage={(e, newPage) => loadReports(newPage)} />

      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })}
        severity={toast.severity} message={toast.message} />

      <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload to {REPO_TABS[tabValue].label}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Title" value={uploadTitle}
            onChange={e => setUploadTitle(e.target.value)} sx={{ mt: 2, mb: 2 }} />
          <TextField fullWidth label="Grade (optional)" value={uploadGrade}
            onChange={e => setUploadGrade(e.target.value)} sx={{ mb: 2 }} />
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Assign To Teacher (optional)</InputLabel>
            <Select
              value={uploadAssignTo}
              label="Assign To Teacher (optional)"
              onChange={e => setUploadAssignTo(e.target.value)}
            >
              <MenuItem value="">— No assignment —</MenuItem>
              {teachers.map(t => (
                <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" component="label" fullWidth>
            {uploadFile ? uploadFile.name : "Choose File"}
            <input type="file" hidden onChange={e => setUploadFile(e.target.files[0])} />
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpload}
            sx={{ background: "linear-gradient(135deg,#4A7BA8,#244C77)" }}>Upload</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminRepository;
